/**
 * Logged-in viewing: the viewer is served on the hub's own site and opened with an exam (`?exam=`), never
 * with a ticket in the link. It asks the hub, with the user's own session cookie, for a ris token, and
 * ris for a short exam ticket; it renews both before the ticket runs out for as long as the user stays
 * logged in. Logging out of the hub (or losing the permission) ends viewing at the next renewal.
 *
 * The ris token and the ticket live in this module's memory only: never in a URL, never in storage.
 */

/** Where the hub is, as seen from the viewer page (same site: the cookie goes along). */
export interface HubSessionConfig {
  /** Origin (+ prefix) of the hub API, e.g. `` (same origin) or `https://api.example.com`. */
  apiRoot: string;
  /** Path of the hub's sign-in page on this site; it takes `?returnTo=` back to the viewer. */
  loginPath: string;
}

export interface OpenedExam {
  studyUIDs: string[];
}

/** The hub has no session for this browser (or it ended): the user has to log in again. */
export class NotLoggedIn extends Error {}

/** ris refused the exam: `status` 404 = not found / no images, 403 = not allowed, 503 = PACS down. */
export class ExamRefused extends Error {
  constructor(readonly status: number, detail: string) {
    super(detail);
  }
}

const RIS_TOKEN_PATH = '/api/v1/auth/token/ris';
const EXAM_TICKET_PATH = '/ris/v1/viewer/exam-tickets';
/** Renew when this share of the ticket's life is gone: early enough to absorb a slow answer. */
const RENEW_AT = 0.8;
/** A renewal that failed for a reason other than the session is retried after this long. */
const RETRY_MS = 30_000;
const MIN_DELAY_MS = 5_000;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

let ticket: string | null = null;
let renewTimer: ReturnType<typeof setTimeout> | null = null;
/** The exam this page opened from the hub session, and where: what "Compartir" shares. */
let opened: { hub: HubSessionConfig; risRoot: string; examId: string } | null = null;

/** The current exam ticket (null before the first one, or once the session ended). */
export function currentTicket(): string | null {
  return ticket;
}

/** The exam opened from the hub session (null on a ticket or share link). */
export function openedExam(): { risRoot: string; examId: string } | null {
  return opened ? { risRoot: opened.risRoot, examId: opened.examId } : null;
}

/**
 * A fresh ris token from the user's hub session, for the ris routes a viewer ticket does not open
 * (making and revoking share links). Never kept: asked for each time.
 */
export async function hubRisToken(): Promise<string> {
  if (!opened) throw new NotLoggedIn();
  return risToken(opened.hub);
}

/** The hub sign-in page, coming back to this very viewer page after login. */
export function loginUrl(hub: HubSessionConfig): string {
  const back = `${window.location.pathname}${window.location.search}`;
  return `${hub.loginPath}?${new URLSearchParams({ returnTo: back }).toString()}`;
}

async function risToken(hub: HubSessionConfig): Promise<string> {
  const response = await fetch(`${hub.apiRoot.replace(/\/$/, '')}${RIS_TOKEN_PATH}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (response.status === HTTP_UNAUTHORIZED || response.status === HTTP_FORBIDDEN) throw new NotLoggedIn();
  if (!response.ok) throw new Error(`hub token ${response.status}`);
  const body = (await response.json()) as { access_token?: unknown };
  if (typeof body.access_token !== 'string' || !body.access_token) throw new Error('hub token missing');
  return body.access_token;
}

/** `renewing`: the ticket in use, as proof that this is the same viewing (ris logs it as a renewal). */
async function examTicket(risRoot: string, token: string, examId: string, renewing: string | null): Promise<{ ticket: string; expiresIn: number; studyUIDs: string[] }> {
  const response = await fetch(`${risRoot.replace(/\/$/, '')}${EXAM_TICKET_PATH}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(renewing ? { accession_id: examId, renewing } : { accession_id: examId }),
  });
  if (response.status === HTTP_UNAUTHORIZED) throw new NotLoggedIn();
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // not JSON
    }
    throw new ExamRefused(response.status, detail);
  }
  const body = (await response.json()) as { ticket: string; expires_in: number; study_uids: string[] };
  return { ticket: body.ticket, expiresIn: body.expires_in, studyUIDs: body.study_uids };
}

function schedule(seconds: number, renew: () => void): void {
  if (renewTimer !== null) clearTimeout(renewTimer);
  renewTimer = setTimeout(renew, Math.max(seconds * 1000, MIN_DELAY_MS));
}

/**
 * Opens the exam from the hub session and keeps its ticket fresh. `onEnded` runs once when a renewal
 * finds the session gone or the exam no longer allowed; the ticket is dropped first, so every request
 * after that is refused rather than served on a stale grant.
 */
export async function openExam(hub: HubSessionConfig, risRoot: string, examId: string, onEnded: () => void): Promise<OpenedExam> {
  const first = await examTicket(risRoot, await risToken(hub), examId, null);
  ticket = first.ticket;
  opened = { hub, risRoot, examId };

  const renew = async (): Promise<void> => {
    try {
      const next = await examTicket(risRoot, await risToken(hub), examId, ticket);
      ticket = next.ticket;
      schedule(next.expiresIn * RENEW_AT, () => void renew());
    } catch (error) {
      if (error instanceof NotLoggedIn || (error instanceof ExamRefused && error.status === HTTP_FORBIDDEN)) {
        ticket = null;
        onEnded();
        return;
      }
      console.warn('[viewer] ticket renewal failed, retrying', error);
      schedule(RETRY_MS / 1000, () => void renew());
    }
  };
  schedule(first.expiresIn * RENEW_AT, () => void renew());
  return { studyUIDs: first.studyUIDs };
}
